import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Dialog, DialogActions, DialogContent, Grid, List, ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { useEffect } from 'react'
import { FieldValues, useForm } from 'react-hook-form';
import { AppDatepicker } from '../../../Component/Common/Components/FormValidationInputs/AppDatepicker';
import { AppQuill } from '../../../Component/Common/Components/FormValidationInputs/AppQuill';
import AppSelectList from '../../../Component/Common/Components/FormValidationInputs/AppSelectList';
import { ISteps } from '../../../Component/Common/Components/Stepper/Stepper';
import { CustomDialogTitle } from '../../Common/Custom/CustomDialogTitle';
import { clientStatusSchema } from './ClientStatusValidationSchema';
import { ApplicationStatusList, DefaultStatusList, EnquiryStatus, InvoiceStatusList, ProposalAgreementStatusList, WrapUpStatusList } from '../../../Component/Common/Constants/Lists/DropdownListOptions';
import { VerificationClientStatus } from '../../Common/StaticData/VAClientStatus';
import { PortalDocumentsDropZone } from '../../Common/PortalDocuments/PortalDocumentsDropZone';
import { RadioButtonChecked } from '@mui/icons-material';
import { truncateSync } from 'fs';

interface IProps {
    showModal: boolean;
    onCloseModal: () => void;
    selectedStep: ISteps;
    updateStatus: (data: FieldValues) => void;
    riskAssessmentFlags?: { text: string; value: boolean }[];
    riskAssessmentNotes?: string;
    link: string;
}
export const StatusDialog = (props: IProps) => {

    const { control, reset, handleSubmit, formState: { isSubmitting } } = useForm({
        mode: 'all',
        resolver: yupResolver(clientStatusSchema)
    });

    const handleDataSubmit = async (data: FieldValues) => {
        const validationData = { ...data };
        props.updateStatus(validationData);
    }

    const resetForm = () => {

        const formData = {
            clientStatus: props.selectedStep.id,
            status: props.selectedStep.state,
            description: props.selectedStep.description,
            date: props.selectedStep.dateAchieved,
            notes: props.riskAssessmentNotes
        };
        reset(formData);
    }

    useEffect(() => {
        resetForm();
    }, [props.selectedStep])

    return (
        <>
            <form id='StatusDialog' onSubmit={handleSubmit(handleDataSubmit)}>
                <Dialog disableEnforceFocus open={props.showModal} onClose={props.onCloseModal} aria-labelledby="form-dialog-title" maxWidth='lg'>
                    <CustomDialogTitle id="individual-dialog-title" onClose={props.onCloseModal}>
                        {props.selectedStep.label}
                    </CustomDialogTitle>
                    <DialogContent >
                        <Grid container width='600px'>
                            <Grid md={12} xs={12}>
                                {(props.selectedStep.id == VerificationClientStatus.Enquiry_Received ) && (
                                    <Box mt={2}>
                                        <AppSelectList control={control} name="status" label="Status" items={EnquiryStatus} disabled={true} />
                                    </Box>
                                )}
                                {(props.selectedStep.id == VerificationClientStatus.Application ) && (
                                    <Box mt={2}>
                                        <AppSelectList control={control} name="status" label="Status" items={ApplicationStatusList} />
                                    </Box>
                                )}
                                {(props.selectedStep.id == VerificationClientStatus.Proposal || props.selectedStep.id == VerificationClientStatus.Verification_Agreement) && (
                                    <Box mt={2}>
                                        <AppSelectList control={control} name="status" label="Status" items={ProposalAgreementStatusList} />
                                    </Box>
                                )}                       
                                {(props.selectedStep.id == VerificationClientStatus.Risk_Assessment || props.selectedStep.id == VerificationClientStatus.Verification_Progress) && (
                                    <Box mt={2}>
                                        <AppSelectList control={control} name="status" label="Status" items={DefaultStatusList} />
                                    </Box>
                                )}
                                {(props.selectedStep.id == VerificationClientStatus.Invoice ) && (
                                    <Box mt={2}>
                                        <AppSelectList control={control} name="status" label="Status" items={InvoiceStatusList} />
                                    </Box>
                                )}                              
                                {(props.selectedStep.id == VerificationClientStatus.Wrap_Up ) && (
                                    <Box mt={2}>
                                        <AppSelectList control={control} name="status" label="Status" items={WrapUpStatusList} />
                                    </Box>
                                )}
                                <Box mt={2}>
                                    <AppDatepicker control={control} name="date" label="Date Achieved" inputFormat='yyyy/MM/dd' />
                                </Box>
                                {props.selectedStep.id === VerificationClientStatus.Risk_Assessment && (
                                    <>
                                        <Box mt={2} sx={{ border: '1px solid #ccc', borderRadius: '5px', padding: '10px' }}>
                                            <List >
                                                <Typography variant="body2" style={{ fontSize: '0.875rem' }}>
                                                    Summary:
                                                </Typography>
                                                {props.riskAssessmentFlags?.filter((field) => field.value).map((field, index) => (
                                                    <ListItem key={index}>
                                                        <ListItemIcon>
                                                            <RadioButtonChecked style={{ fontSize: '16px', marginRight: '10px', color: '#000' }} />
                                                        </ListItemIcon>
                                                        <ListItemText>
                                                            <Typography variant="body2" style={{ fontSize: '0.875rem' }}>
                                                                {field.text}
                                                            </Typography>
                                                        </ListItemText>
                                                    </ListItem>
                                                ))}
                                            </List>
                                            <Box mt={2} >
                                                <Typography variant="body2" style={{ fontSize: '0.875rem' }}>
                                                    The following note(s) were provided:
                                                </Typography>
                                                <AppQuill control={control} name={"notes"} placeholder="Notes:" disabled={true} />
                                            </Box>
                                        </Box>
                                    </>
                                )}
                                {
                                    props.selectedStep.recordId > 0 &&
                                    (props.selectedStep.id === VerificationClientStatus.Verification_Agreement || props.selectedStep.id === VerificationClientStatus.Invoice) &&
                                    <Box mt={2}>
                                        <PortalDocumentsDropZone link={props.link} id={props.selectedStep.recordId} />
                                    </Box>
                                }
                                {(
                                    (props.selectedStep.id > VerificationClientStatus.Application && props.selectedStep.id <= VerificationClientStatus.Proposal) || 
                                    props.selectedStep.id == VerificationClientStatus.Verification_Agreement || props.selectedStep.id === VerificationClientStatus.Invoice) && (
                                        <Box mt={2}>
                                            <AppQuill control={control} name={"description"} placeholder="Description:" defaultValue={props.selectedStep.description || ''} />
                                        </Box>
                                    )}
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <LoadingButton loading={isSubmitting} variant='contained' form='StatusDialog' type="submit" color='primary'>
                            Save
                        </LoadingButton>
                    </DialogActions>
                </Dialog>
            </form>
        </>
    )
}
